import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useGetMyChildCoparentsQuery } from '../../store/api/localApi';
import { FeesSection } from '../../components/students/FeesSection';
import { ChildMedicalSection } from '../../components/parent/ChildMedicalSection';
import { CoparentsCard } from '../../components/parent/CoparentsCard';
import ParentLeftChildBanner from '../../components/parent/ParentLeftChildBanner';

/**
 * /parent/:studentId/details — the child's record: who the parents and
 * guardians are, fees, medical and GP details, and the pickup list. These
 * lived on the child home; the home is now today + what needs the parent.
 */
const ParentChildDetails: React.FC = () => {
  const { activeStudent, isLoading, parentHomePath } = useActiveStudent();
  const { data: coparentsResponse } = useGetMyChildCoparentsQuery(
    { childId: activeStudent?.id ?? '' },
    { skip: !activeStudent }
  );
  if (isLoading)
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  if (!activeStudent) return <Navigate to={parentHomePath ?? '/parent'} replace />;
  const hasLeft = activeStudent.status === 'LEFT';
  return (
    <div className="py-6" data-testid="parent-child-details">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        <div>
          <Link
            to={`/parent/${activeStudent.id}`}
            data-testid="parent-details-back"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Back to {activeStudent.firstName || 'home'}
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">{activeStudent.displayName}</h1>
          <p className="text-sm text-gray-500">
            {activeStudent.locationName}
            {activeStudent.grade ? ` · ${activeStudent.grade}` : ''}
          </p>
        </div>
        {hasLeft && (
          <ParentLeftChildBanner
            childName={activeStudent.firstName || activeStudent.displayName}
            locationName={activeStudent.locationName}
          />
        )}
        <CoparentsCard
          childId={activeStudent.id}
          childName={activeStudent.firstName || 'this child'}
          coparents={coparentsResponse?.coparents ?? []}
          hasLeft={hasLeft}
        />
        <Link
          to="/parent/account/pickup-contacts"
          data-testid="parent-child-profile-link"
          className="bg-white rounded-lg shadow p-4 flex items-center justify-between hover:shadow-md"
        >
          <span className="text-sm font-semibold text-gray-900">Authorised pickup</span>
          <ArrowRightIcon className="h-4 w-4 text-gray-400" />
        </Link>
        <section
          data-testid="fees-section-wrapper"
          className="bg-white rounded-lg shadow px-4 py-4"
        >
          <FeesSection studentId={activeStudent.id} isAdmin={false} />
        </section>
        <ChildMedicalSection studentId={activeStudent.id} />
      </div>
    </div>
  );
};
export default ParentChildDetails;
